import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import _ from "lodash";
import { Fragment, type ReactNode } from "react";
import ConversationDayDivider, {
  withConversationDays,
} from "messaging/correspondence/_views/conversation-day-divider";
import { useSelector } from "react-redux";
import { getCommentsByProposal, getIsLoadingComments } from "work/proposals/comments/_store/comments-redux-slice";
import type Comment from "work/proposals/comments/comment";
import { ProposalEmptyPaneMessage } from "work/proposals/_views/_shared/proposal-styled-components";

// Chat bubbles, not list rows: a plain block, as the work agreement's chat lists its messages.
const CommentList = styled("div")(() => ({}));

/**
 * Oldest first, by the moment each comment entered the conversation — bottom-up, the same order every
 * chat and correspondence pane reads in (#5741). A comment that has not entered it yet — the one the
 * author just posted, still on the wire — is the newest thing here rather than the oldest, so it sorts
 * below everything instead of jumping away from the foot of the list once the server answers (#5125).
 * See `Comment.enteredConversationAt`.
 */
export function sortCommentsOldestFirst(comments: readonly Comment[] | undefined): Comment[] {
  return _(comments)
    .uniqBy((comment) => comment.id?.value)
    .orderBy((comment) => comment.enteredConversationAt?.valueOf() ?? Number.POSITIVE_INFINITY, "asc")
    .value();
}

/**
 * The moment a comment entered the conversation, or now for the one still on the wire — the moment
 * `sortCommentsOldestFirst` puts it at, and the one `CommentItem` dates its bubble with.
 */
export function conversationMomentOf(comment: Comment): AHDate {
  return comment.enteredConversationAt ?? new AHDate();
}

type ProposalCommentListProps = {
  /** The comment store's key for the conversation: the negotiation id the comment forums are filed under. */
  conversationId: Guid | undefined;
  /** Draws one comment. The host decides what its reader may do with it. */
  renderComment: (comment: Comment) => ReactNode;
  emptyText: string;
  /** Whether a comment is listed; the pane's date filter. Everything is listed without one. */
  include?: (comment: Comment) => boolean;
};

/**
 * One negotiation's conversation, oldest first, and nothing else (#5398).
 *
 * The Comments pane draws this. It used to draw the conversation zipped in with the change history,
 * which put a line about every save between the comments and drew both with the same colored rule —
 * so a change read as a comment nobody wrote. The history has its own drawer now.
 *
 * Unpublished and withheld comments are listed like any other. The store only holds what the server
 * served this reader: an author-only draft reaches its author and nobody else, and the only surface
 * that can publish or delete it again is this one. A withheld comment is the tombstone the server
 * serves in its place (#4564), which `CommentItem` draws.
 */
export default function ProposalCommentList(props: Readonly<ProposalCommentListProps>) {
  const { conversationId, renderComment, emptyText, include } = props;
  const comments = useSelector(getCommentsByProposal(conversationId));
  const isLoading = useSelector(getIsLoadingComments(conversationId));
  const sortedComments = sortCommentsOldestFirst(include ? comments?.filter(include) : comments);

  // Only while there is nothing to show: a refetch after a post or a realtime notice must not blank the
  // list and take the row an author is working on out of reach.
  if (isLoading && sortedComments.length === 0) return <Loader />;
  if (sortedComments.length === 0) return <ProposalEmptyPaneMessage>{emptyText}</ProposalEmptyPaneMessage>;

  return (
    <CommentList data-testid="proposal-comment-list">
      {/* A divider above the first comment of each day, which the bubbles under it then leave out (#7082).
          Dated on the moment the list sorts on, so a day's comments never straddle its divider. */}
      {withConversationDays(sortedComments, conversationMomentOf).map(({ item: comment, opensDay }, index) => (
        <Fragment key={comment.id?.value ?? `unsaved-${index}`}>
          {opensDay && <ConversationDayDivider date={opensDay} />}
          {renderComment(comment)}
        </Fragment>
      ))}
    </CommentList>
  );
}
